/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  gym: {
    all: ["gym"] as const,
    /** Visible exercise catalog (global + custom). */
    exercises: (userId: string | undefined, includeArchived?: boolean) =>
      ["gym", "exercises", userId, includeArchived] as const,
    /** User routines with exercise count. */
    routines: (userId: string | undefined) => ["gym", "routines", userId] as const,
    /** Trash: custom exercises with soft-delete. */
    trashedExercises: (userId: string | undefined) =>
      ["gym", "exercises", "trashed", userId] as const,
    /** Trash: routines with soft-delete. */
    trashedRoutines: (userId: string | undefined) =>
      ["gym", "routines", "trashed", userId] as const,
    /** Detail of a routine. */
    routineDetail: (routineId: string | undefined) =>
      ["gym", "routines", "detail", routineId] as const,
    /** Routine exercises (ordered) with metadata. */
    routineExercises: (routineId: string | undefined) =>
      ["gym", "routineExercises", routineId] as const,
    /** LOCAL version history of a routine (bounded bucket). */
    routineVersions: (routineId: string | undefined) =>
      ["gym", "routineVersions", routineId] as const,
    /** SEALED version of a session (fetched via API for history). */
    sessionSealedVersion: (sessionId: string | undefined) =>
      ["gym", "sessionSealedVersion", sessionId] as const,
    /** Most recent open session (finished_at IS NULL) of the user. */
    openSession: (userId: string | undefined) => ["gym", "openSession", userId] as const,
    /** Session history with aggregated stats. */
    sessions: (userId: string | undefined, limit: number) =>
      ["gym", "sessions", userId, limit] as const,
    /** Detail of a session. */
    sessionDetail: (sessionId: string | undefined) =>
      ["gym", "sessions", "detail", sessionId] as const,
    /** Sets of a session with exercise metadata. */
    sessionSets: (sessionId: string | undefined) => ["gym", "sessionSets", sessionId] as const,
    /** Full set history of ONE exercise (PRs/charts/preload). */
    exerciseHistory: (userId: string | undefined, exerciseId: string | undefined) =>
      ["gym", "exerciseHistory", userId, exerciseId] as const,
    /** Sets since a date with muscle group (weekly summary). */
    setsSince: (userId: string | undefined, sinceISO: string) =>
      ["gym", "setsSince", userId, sinceISO] as const,
    /** User body measurements. */
    bodyMeasurements: (userId: string | undefined, limit?: number) =>
      ["gym", "bodyMeasurements", userId, limit] as const,
  },
  powersync: {
    /** Upload queue stats (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** Is there local data? (cleanup button in profile). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (getThemesQuery without pagination; profile + list-dialog SHARE the cache). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
    /**
     * Active sessions list of the profile.
     *
     * `all()` is the PREFIX (to invalidate all variants) and
     * `forCurrent()` is the exact key used by the query, which carries the current
     * session id to distinguish "this device". P23/CC-60 · phase 3:
     * revocation needs to invalidate by prefix, and the lint guard forbids
     * writing the key inline.
     */
    activeSessions: {
      all: () => ["active-sessions"] as const,
      forCurrent: (currentSessionId: string | undefined) =>
        ["active-sessions", currentSessionId] as const,
    },
  },
};
